import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import AdminManageMenu from "../components/AdminManageMenu";
import {
  Briefcase, MapPin, Clock, DollarSign, Search,
  Plus, Eye, CheckCircle2, Handshake, MessageSquare, Calendar as CalendarIcon
} from "lucide-react";

const JOB_TYPES = [
  { value: "full-time", label: "Full-Time" },
  { value: "part-time", label: "Part-Time" },
  { value: "temporary", label: "Temporary" },
  { value: "contract", label: "Contract" },
  { value: "casual", label: "Casual" },
];

const WORK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TYPE_COLORS = {
  "full-time": "#10B981", "part-time": "#3B82F6", "temporary": "#8DB5A0",
  "contract": "#8DB5A0", "casual": "#8B5CF6"
};

const EMPTY_FORM = {
  title: "", location: "", province: "AB", job_type: "full-time",
  description: "", pay_range: "", hours: "",
  open_to_commission: false,
  available_work_days: [],
  hours_per_day: "",
  hours_per_week: "",
  open_to_discussion: false,
  clinic_contact_email: "",
};

const JobBoardPage = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterProvince, setFilterProvince] = useState("");
  const [filterType, setFilterType] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Express Interest dialog
  const [showInterest, setShowInterest] = useState(false);
  const [interestMessage, setInterestMessage] = useState("");
  const [expressing, setExpressing] = useState(false);

  // Admin: see who expressed interest
  const [showInterestList, setShowInterestList] = useState(false);
  const [interestList, setInterestList] = useState([]);

  useEffect(() => {
    fetchJobs();
    axios.get(`${API}/provinces`).then(r => setProvinces(r.data)).catch(() => {});
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterProvince) params.append("province", filterProvince);
      if (filterType) params.append("job_type", filterType);
      const res = await axios.get(`${API}/jobs?${params}`);
      setJobs(res.data);
    } catch { toast.error("Failed to load jobs"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchJobs(); /* eslint-disable-next-line */ }, [filterProvince, filterType]);

  const handleSearch = (e) => { e.preventDefault(); fetchJobs(); };

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      available_work_days: f.available_work_days.includes(day)
        ? f.available_work_days.filter(d => d !== day)
        : [...f.available_work_days, day]
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title || !form.location || !form.description || !form.pay_range) {
      toast.error("Fill in required fields"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/jobs`, form);
      toast.success("Job posted!");
      setShowCreate(false);
      setForm(EMPTY_FORM);
      fetchJobs();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to post"); }
    finally { setSubmitting(false); }
  };

  const handleViewJob = async (jobId) => {
    try {
      const res = await axios.get(`${API}/jobs/${jobId}`);
      setSelectedJob(res.data);
    } catch { toast.error("Failed to load job"); }
  };

  const handleExpressInterest = async () => {
    if (!selectedJob) return;
    setExpressing(true);
    try {
      const res = await axios.post(`${API}/jobs/${selectedJob.job_id}/express-interest`, {
        message: interestMessage.trim() || null,
      });
      if (res.data.already_expressed) {
        toast.info("You already expressed interest in this job.");
      } else {
        toast.success("Interest sent! The clinic and our team have been notified.");
      }
      setSelectedJob(prev => prev ? { ...prev, has_expressed_interest: true, interests_count: (prev.interests_count || 0) + 1 } : prev);
      setJobs(prev => prev.map(j => j.job_id === selectedJob.job_id
        ? { ...j, has_expressed_interest: true, interests_count: (j.interests_count || 0) + 1 }
        : j));
      setInterestMessage("");
      setShowInterest(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not express interest");
    } finally {
      setExpressing(false);
    }
  };

  const handleWithdrawInterest = async () => {
    if (!selectedJob) return;
    if (!window.confirm("Withdraw your expression of interest?")) return;
    try {
      await axios.delete(`${API}/jobs/${selectedJob.job_id}/express-interest`);
      toast.success("Interest withdrawn");
      setSelectedJob(prev => prev ? { ...prev, has_expressed_interest: false, interests_count: Math.max(0, (prev.interests_count || 0) - 1) } : prev);
      setJobs(prev => prev.map(j => j.job_id === selectedJob.job_id
        ? { ...j, has_expressed_interest: false, interests_count: Math.max(0, (j.interests_count || 0) - 1) }
        : j));
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const fetchInterestList = async () => {
    if (!selectedJob) return;
    try {
      const res = await axios.get(`${API}/jobs/${selectedJob.job_id}/interests`);
      setInterestList(res.data || []);
      setShowInterestList(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load interest list");
    }
  };

  const isAdmin = user?.is_admin;
  const timeAgo = (date) => {
    const d = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
    if (d === 0) return "Today";
    if (d === 1) return "Yesterday";
    return `${d} days ago`;
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="job-board-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <EditableText
              blockKey="jobs.title"
              fallback="Job Board"
              as="h1"
              className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            />
            <EditableText
              blockKey="jobs.subtitle"
              fallback="Dental hygiene opportunities across Canada — tap Express Interest and the clinic will reach out."
              as="p"
              className="text-[#64748B] mt-1"
              multiline
            />
          </div>
          {isAdmin && (
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="post-job-btn">
                  <Plus className="w-4 h-4 mr-2" /> Post Job
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Post a Job</DialogTitle></DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                  <div><Label>Job Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. Registered Dental Hygienist" className="mt-2 bg-white border-[#E5DFEC]" data-testid="job-title-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Town/City *</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="e.g. Calgary" className="mt-2 bg-white border-[#E5DFEC]" data-testid="job-location-input" /></div>
                    <div><Label>Province</Label>
                      <Select value={form.province} onValueChange={v => setForm({...form, province: v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                        <SelectContent>{provinces.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent></Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Type</Label>
                      <Select value={form.job_type} onValueChange={v => setForm({...form, job_type: v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                        <SelectContent>{JOB_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div><Label>Pay Range *</Label><Input value={form.pay_range} onChange={e => setForm({...form, pay_range: e.target.value})} placeholder="e.g. $45-55/hr" className="mt-2 bg-white border-[#E5DFEC]" data-testid="job-pay-input" /></div>
                  </div>
                  <div><Label>General Hours (legacy text)</Label><Input value={form.hours} onChange={e => setForm({...form, hours: e.target.value})} placeholder="e.g. Mon-Fri 8am-4pm" className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Hours / Day</Label><Input value={form.hours_per_day} onChange={e => setForm({...form, hours_per_day: e.target.value})} placeholder="e.g. 6-8" className="mt-2 bg-white border-[#E5DFEC]" data-testid="job-hours-day-input" /></div>
                    <div><Label>Hours / Week</Label><Input value={form.hours_per_week} onChange={e => setForm({...form, hours_per_week: e.target.value})} placeholder="e.g. 30-40" className="mt-2 bg-white border-[#E5DFEC]" data-testid="job-hours-week-input" /></div>
                  </div>
                  <div>
                    <Label>Available Work Days</Label>
                    <div className="flex flex-wrap gap-2 mt-2" data-testid="job-days-picker">
                      {WORK_DAYS.map(d => {
                        const selected = form.available_work_days.includes(d);
                        return (
                          <button
                            type="button"
                            key={d}
                            onClick={() => toggleDay(d)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              selected ? 'bg-[#6B4E8A] text-white' : 'bg-[#F0ECF7] text-[#6B4E8A] hover:bg-[#E5DFEC]'
                            }`}
                            data-testid={`job-day-${d}`}
                          >
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 bg-[#FAF7F2] border border-[#E5DFEC] rounded-xl p-3">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-[#475569]">
                      <Checkbox
                        checked={form.open_to_commission}
                        onCheckedChange={(c) => setForm(f => ({ ...f, open_to_commission: !!c }))}
                        data-testid="job-open-commission"
                      />
                      <Handshake className="w-4 h-4 text-[#8DB5A0]" /> Open to commission
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-[#475569]">
                      <Checkbox
                        checked={form.open_to_discussion}
                        onCheckedChange={(c) => setForm(f => ({ ...f, open_to_discussion: !!c }))}
                        data-testid="job-open-discussion"
                      />
                      <MessageSquare className="w-4 h-4 text-[#8DB5A0]" /> Open to discussion (negotiable rate / schedule)
                    </label>
                  </div>
                  <div>
                    <Label>Clinic Contact Email <span className="text-[#94A3B8] font-normal">(receives interest notifications)</span></Label>
                    <Input
                      type="email"
                      value={form.clinic_contact_email}
                      onChange={e => setForm({ ...form, clinic_contact_email: e.target.value })}
                      placeholder="owner@clinic.ca"
                      className="mt-2 bg-white border-[#E5DFEC]"
                      data-testid="job-clinic-email-input"
                    />
                  </div>
                  <div><Label>Description *</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Role details, requirements, benefits..." className="mt-2 bg-white border-[#E5DFEC] min-h-[120px]" data-testid="job-description-input" /></div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="rounded-full">Cancel</Button>
                    <Button type="submit" className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" disabled={submitting} data-testid="submit-job-btn">{submitting ? "Posting..." : "Post Job"}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <form onSubmit={handleSearch} className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search jobs..." className="pl-10 bg-white border-[#E5DFEC]" data-testid="job-search-input" />
          </form>
          <Select value={filterProvince || "all"} onValueChange={v => setFilterProvince(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 bg-white border-[#E5DFEC]"><SelectValue placeholder="Province" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Provinces</SelectItem>{provinces.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterType || "all"} onValueChange={v => setFilterType(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 bg-white border-[#E5DFEC]"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem>{JOB_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}</div>
        ) : jobs.length > 0 ? (
          <div className="space-y-4">
            {jobs.map((job, idx) => (
              <motion.div
                key={job.job_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="bg-white border border-[#E5DFEC] rounded-2xl p-5 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleViewJob(job.job_id)}
                data-testid={`job-card-${job.job_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{job.title}</h3>
                      <span className="px-2.5 py-0.5 text-xs font-medium rounded-full text-white" style={{ backgroundColor: TYPE_COLORS[job.job_type] || "#64748B" }}>{job.job_type}</span>
                      {job.open_to_commission && <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-[#8DB5A0]/15 text-[#558471] inline-flex items-center gap-1"><Handshake className="w-3 h-3" />Commission OK</span>}
                      {job.open_to_discussion && <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-[#3B82F6]/10 text-[#3B82F6] inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />Negotiable</span>}
                      {job.has_expressed_interest && <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-[#10B981]/10 text-[#10B981] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Interested</span>}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-[#64748B]">
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{job.location}</span>
                      <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />{job.pay_range}</span>
                      {(job.hours_per_week || job.hours) && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{job.hours_per_week ? `${job.hours_per_week}h/wk` : job.hours}</span>}
                      {job.available_work_days?.length > 0 && <span className="flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5" />{job.available_work_days.join(", ")}</span>}
                    </div>
                    <p className="text-sm text-[#64748B] mt-2 line-clamp-2">{job.description}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-[#64748B]">{timeAgo(job.created_at)}</span>
                    <span className="flex items-center gap-1 text-xs text-[#64748B]"><Eye className="w-3 h-3" />{job.views}</span>
                    <div onClick={(e) => e.stopPropagation()}>
                      <AdminManageMenu
                        resourceType="job"
                        resourceId={job.job_id}
                        resourceLabel={job.title}
                        onDeleted={fetchJobs}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
            <Briefcase className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
            <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No job listings yet</h3>
            <p className="text-[#64748B]">{search || filterProvince || filterType ? "Try different search filters" : "Check back soon for new opportunities"}</p>
          </div>
        )}

        {/* Job Detail Dialog */}
        <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            {selectedJob && (
              <>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedJob.title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 text-sm font-medium rounded-full text-white" style={{ backgroundColor: TYPE_COLORS[selectedJob.job_type] || "#64748B" }}>{selectedJob.job_type}</span>
                    {selectedJob.open_to_commission && <span className="px-3 py-1 text-xs font-medium rounded-full bg-[#8DB5A0]/15 text-[#558471] inline-flex items-center gap-1"><Handshake className="w-3 h-3" />Open to commission</span>}
                    {selectedJob.open_to_discussion && <span className="px-3 py-1 text-xs font-medium rounded-full bg-[#3B82F6]/10 text-[#3B82F6] inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />Open to discussion</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-[#64748B]"><MapPin className="w-4 h-4" /><span>{selectedJob.location}</span></div>
                    <div className="flex items-center gap-2 text-[#64748B]"><DollarSign className="w-4 h-4" /><span>{selectedJob.pay_range}</span></div>
                    {selectedJob.hours_per_day && <div className="flex items-center gap-2 text-[#64748B]"><Clock className="w-4 h-4" /><span>{selectedJob.hours_per_day} hrs / day</span></div>}
                    {selectedJob.hours_per_week && <div className="flex items-center gap-2 text-[#64748B]"><Clock className="w-4 h-4" /><span>{selectedJob.hours_per_week} hrs / week</span></div>}
                    {selectedJob.hours && !selectedJob.hours_per_week && <div className="flex items-center gap-2 text-[#64748B] col-span-2"><Clock className="w-4 h-4" /><span>{selectedJob.hours}</span></div>}
                    {selectedJob.available_work_days?.length > 0 && (
                      <div className="flex items-center gap-2 text-[#64748B] col-span-2"><CalendarIcon className="w-4 h-4" /><span>{selectedJob.available_work_days.join(", ")}</span></div>
                    )}
                  </div>
                  <div className="bg-[#FAF7F2] rounded-xl p-4">
                    <p className="text-sm text-[#6B4E8A] whitespace-pre-wrap">{selectedJob.description}</p>
                  </div>

                  {selectedJob.has_expressed_interest ? (
                    <Button
                      onClick={handleWithdrawInterest}
                      variant="outline"
                      className="w-full rounded-full border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5 py-5"
                      data-testid="withdraw-interest-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      You're interested — tap to withdraw
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setShowInterest(true)}
                      className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full py-5"
                      data-testid="express-interest-btn"
                    >
                      <Handshake className="w-4 h-4 mr-2" />
                      Express Interest
                    </Button>
                  )}

                  {isAdmin && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={fetchInterestList}
                      className="w-full rounded-full text-xs"
                      data-testid="admin-view-interests-btn"
                    >
                      View interest ledger ({selectedJob.interests_count || 0})
                    </Button>
                  )}

                  <p className="text-xs text-[#64748B] text-center">
                    {selectedJob.interests_count > 0 && `${selectedJob.interests_count} ${selectedJob.interests_count === 1 ? "person has" : "people have"} expressed interest · `}
                    Posted {timeAgo(selectedJob.created_at)}
                  </p>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Express Interest dialog */}
        <Dialog open={showInterest} onOpenChange={(v) => { setShowInterest(v); if (!v) setInterestMessage(""); }}>
          <DialogContent className="max-w-md" data-testid="express-interest-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Express Interest</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-[#64748B]">
                We'll let the clinic know you're interested and share your DHDH profile basics (name, email, province, registration #). You can add a quick note below.
              </p>
              <Textarea
                value={interestMessage}
                onChange={(e) => setInterestMessage(e.target.value)}
                placeholder="Optional: a short note to the clinic owner…"
                maxLength={600}
                rows={4}
                className="bg-white border-[#E5DFEC]"
                data-testid="express-interest-message"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowInterest(false)} className="rounded-full">Cancel</Button>
                <Button
                  onClick={handleExpressInterest}
                  disabled={expressing}
                  className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F]"
                  data-testid="submit-express-interest-btn"
                >
                  {expressing ? "Sending…" : "Send Interest"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Admin: interest ledger dialog */}
        <Dialog open={showInterestList} onOpenChange={setShowInterestList}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="interest-ledger-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                Interest Ledger {selectedJob ? `— ${selectedJob.title}` : ""}
              </DialogTitle>
            </DialogHeader>
            {interestList.length === 0 ? (
              <p className="text-sm text-[#64748B] py-6 text-center">No one has expressed interest yet.</p>
            ) : (
              <div className="space-y-2 mt-3">
                {interestList.map((i) => (
                  <div key={i.interest_id} className="bg-[#FAF7F2] rounded-xl p-3 text-sm" data-testid={`interest-row-${i.interest_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-[#6B4E8A]">{i.user_name}</span>
                      <span className="text-xs text-[#94A3B8]">{new Date(i.created_at).toLocaleString("en-CA")}</span>
                    </div>
                    <div className="text-xs text-[#64748B] mt-1 space-y-0.5">
                      <p>{i.user_email}</p>
                      {i.user_province && <p>Province: {i.user_province}</p>}
                      {i.user_registration_number && <p>Reg #: {i.user_registration_number}</p>}
                    </div>
                    {i.message && (
                      <p className="text-xs italic text-[#475569] mt-2 border-l-2 border-[#8DB5A0] pl-2">{i.message}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default JobBoardPage;
